import { CommonModule, } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzCascaderModule } from 'ng-zorro-antd/cascader';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { AuthService } from 'src/app/Auth/AuthService';
import { ImageCropperComponent, ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';

@Component({
  selector: 'app-company',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, NzModalModule, NzCascaderModule, ImageCropperComponent],
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss', '../../../../auth.scss'],
})
export class CompanyComponent implements OnInit {

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  borderColor = '#98A2B3';

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.borderColor = '#6722FB';
  }

  onDragLeave(): void {
    this.borderColor = '#98A2B3';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.borderColor = '#98A2B3';

    if (event.dataTransfer?.files?.length) {
      this.fileInput.nativeElement.files = event.dataTransfer.files;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
    }
  }

  profileImageSrc: any;

  // Image cropper variables
  cropperModalVisible = false;
  imageChangedEvent: Event | null = null;
  croppedImage: string = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: Router,
    private activaterouter: ActivatedRoute
  ) { }
  sharedService = inject(SharedService);
  pagesService = inject(PagesService);
  CompanyForm!: FormGroup;
  @ViewChild('profileFileInput')
  profileFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('prizeFileInput') prizeFileInput!: ElementRef<HTMLInputElement>;
  imagePreview: { [key: string]: string | ArrayBuffer | null } = {};
  fileData: any;
  planData: any = null;
  sectorsList: any[] = [];
  sectorsTree: any[] = [];
  
  ngOnInit(): void {
    this.CompanyForm = this.fb.group({
      business_logo: [[]],
      name: ['', [Validators.required]],
      address: [''],
      sector_id: [[]],
    });
    this.loadSectors();
    this.activaterouter.queryParams.subscribe((params) => {
      const token = params['token'];
      if (token) {
        try {
          const decoded = atob(token);
          this.planData = JSON.parse(decoded);
        } catch (err) {
          console.error('Invalid token format', err);
          this.route.navigate(['/pricing']);
        }
      } else {
        this.route.navigate(['/pricing']);
      }
    });
  }

  goBack(): void {
    const token = this.createSecretToken(this.planData);
    this.route.navigate(['/auth/signup'], { queryParams: { token } });
  }
  
  createSecretToken(plan: any): string {
    const payload = plan;
    return btoa(JSON.stringify(payload));
  }

  loadSectors(): void {
    this.authService.getAll('GET_SECTORS').subscribe({
      next: (response: any) => {
        this.sectorsList = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];
        this.sectorsTree = this.buildSectorTree(this.sectorsList);
      },
      error: (err) => {
        console.error('Failed to load sectors', err);
      }
    });
  }

  buildSectorTree(flatList: any[]): any[] {
    const map = new Map<number, any>();
    const roots: any[] = [];

    // Only show active sectors — inactive ones must not be selectable
    const activeList = flatList.filter((item: any) => item.is_active);

    activeList.forEach(item => {
      map.set(item.id, {
        value: item.id,
        label: item.name,
        children: [],
        isLeaf: true
      });
    });

    activeList.forEach(item => {
      const node = map.get(item.id);
      if (!item.parent_id || item.parent_id === 0) {
        roots.push(node);
      } else {
        const parent = map.get(item.parent_id);
        if (parent) {
          parent.children.push(node);
          parent.isLeaf = false;
        }
      }
    });

    const cleanLeaves = (nodes: any[]) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanLeaves(node.children);
        }
      });
    };
    cleanLeaves(roots);

    return roots;
  }

  extractSectorIds(cascaderValue: any): number[] | null {
    if (!cascaderValue || !Array.isArray(cascaderValue) || cascaderValue.length === 0) {
      return null;
    }
    return cascaderValue.map((path: any): number => {
      if (Array.isArray(path) && path.length > 0) {
        return path[path.length - 1] as number;
      }
      return path as number;
    });
  }

  onSubmit() {
    this.sharedService.showLoader();
    this.CompanyForm.markAllAsTouched();
    
    if (this.CompanyForm.valid) {
      // ✅ CHANGE 1: Better check for logo existence
      const logo = this.fileData?.['business_logo'];

      // ✅ CHANGE 2: Explicitly set empty array if no logo
      this.CompanyForm.get('business_logo')?.setValue(
        logo ? (Array.isArray(logo) ? logo : [logo]) : []
      );

      const formValue = { ...this.CompanyForm.value };
      formValue.sector_id = this.extractSectorIds(formValue.sector_id);
      console.log('SET_COMPANY_DETAILS payload:', formValue);

      this.authService
        .login('SET_COMPANY_DETAILS', formValue)
        .subscribe({
          next: (response: any) => {
            if (!response?.isSuccess && response?.isSuccess !== undefined) {
              this.sharedService.createNotification('error', response.message);
              this.sharedService.hideLoader();
              return;
            }
            this.sharedService.createNotification(
              'success',
              'Company Added Successfully'
            );

            // Send OTP after company details are saved
            const currentLang = localStorage.getItem('lang') || 'en';
            this.pagesService.update('SEND_OPT', { language: currentLang, lang: currentLang }).subscribe({
              next: (otpResponse: any) => {
                // Store OTP details in localStorage
                localStorage.setItem('optmail', otpResponse.email);
                localStorage.setItem('otp_expiry', otpResponse.otp_expiry_timestamp);

                // Close any open modals before navigating to avoid them persisting on the next page
                this.sharedService.closeAllModals();

                // Navigate to OTP verification page
                const token = this.createSecretToken(this.planData);
                this.route.navigate(['/auth/verify-otp'], { queryParams: { token } });

                // ✅ CHANGE 3: Proper cleanup after submission
                this.CompanyForm.reset();
                this.imagePreview = {};
                this.fileData = {};
                this.sharedService.hideLoader();
              },
              error: (otpErr) => {
                this.sharedService.createNotification(
                  'error',
                  'Failed to send OTP'
                );
                this.sharedService.hideLoader();
                console.error(otpErr);
              }
            });
          },
          error: (err) => {
            this.sharedService.createNotification(
              'error',
              'Failed to add company'
            );
            this.sharedService.hideLoader();
            console.error(err);
          },
        });
    } else {
      this.CompanyForm.markAllAsTouched();
      this.sharedService.hideLoader();
    }
  }

  triggerProfileFileInput(): void {
    this.profileFileInput.nativeElement.click();
  }

  onProfileFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      this.sharedService.createNotification('error', 'Only JPG and PNG images are allowed.');
      input.value = '';
      return;
    }

    const maxFileSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxFileSize) {
      this.CompanyForm.get('business_logo')?.setErrors({ fileSize: true });
      this.sharedService.createNotification('error', 'The image file size must not exceed 5 MB.');
      input.value = '';
      return;
    }

    // Open cropper modal
    this.imageChangedEvent = event;
    this.cropperModalVisible = true;
  }

  imageCropped(event: ImageCroppedEvent) {
    if (event.blob) {
      const reader = new FileReader();
      reader.onload = () => {
        this.croppedImage = reader.result as string;
      };
      reader.readAsDataURL(event.blob);
    }
  }

  imageLoaded(image: LoadedImage) {}

  cropperReady() {}

  loadImageFailed() {
    this.sharedService.createNotification('error', 'Failed to load image. Please try another file.');
    this.cropperModalVisible = false;
  }

  confirmCrop() {
    if (this.croppedImage) {
      if (!this.fileData) {
        this.fileData = {};
      }

      const fileMetadata = {
        path: this.croppedImage,
        url: this.croppedImage,
        name: 'cropped-logo.png',
        type: 'image/png',
        size: 0,
        mime: 0,
        meta: 0,
      };

      this.fileData['business_logo'] = fileMetadata;

      if (!this.imagePreview) {
        this.imagePreview = {};
      }
      this.imagePreview['business_logo'] = this.croppedImage;

      const control = this.CompanyForm.get('business_logo');
      control?.setValue([fileMetadata]);
      control?.markAsTouched();
      control?.updateValueAndValidity();

      this.sharedService.createNotification('success', 'Image cropped successfully.');
    }
    this.cropperModalVisible = false;
    this.imageChangedEvent = null;
    if (this.profileFileInput) {
      this.profileFileInput.nativeElement.value = '';
    }
  }

  cancelCrop() {
    this.cropperModalVisible = false;
    this.imageChangedEvent = null;
    this.croppedImage = '';
    if (this.profileFileInput) {
      this.profileFileInput.nativeElement.value = '';
    }
  }

  // ✅ CHANGE 4: Complete rewrite of removeImage function
  removeImage(controlName: string): void {
    // Clear image preview
    this.imagePreview[controlName] = null;
    
    // Reset form control properly
    this.CompanyForm.get(controlName)?.reset();
    this.CompanyForm.get(controlName)?.setValue([]);
    
    // ✅ CRITICAL FIX: Clear fileData object
    if (this.fileData && this.fileData[controlName]) {
      delete this.fileData[controlName];
    }

    // Clear file input element
    const input = document.getElementById('logo') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
    
    // ✅ NEW: Clear ViewChild reference
    if (this.profileFileInput?.nativeElement) {
      this.profileFileInput.nativeElement.value = '';
    }
  }
}
